import type { FC, HTMLAttributes } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedDate, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { Tooltip } from '../../../../components/GraphModal/Tooltip';
import { ModerationUserVisibilityOnlyInfos } from '../../../../components/Moderation/ModerationInfos';
import { dsaClassHandler } from '../../../../contributor/Profile/DSA/dsaClassHandler';
import { DsaMessage } from '../../../../contributor/Profile/DSA/DsaMessage';
import { ProfilePopover } from '../../../../contributor/Profile/ProfilePopover';
import { useConnectedRouter } from '../../../../hooks/useConnectedRouter';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useWebsite } from '../../../../hooks/useWebsite';
import type { Topic, User, Vote } from '../../../../types/api';
import { getLocaleDateDigits } from '../../../../utils/date';
import type { EjsHighlightedEmbed } from '../../../../utils/editor';
import { getCardViewContent, getEjsHighlightedEmbed } from '../../../../utils/editor';
import { parseJSONString } from '../../../../utils/json';
import { AuthorAvatar } from '../../../AuthorAvatar';
import { EmbedsOverview } from '../../../Content/EmbedsOverview';
import { getAuthorPseudo } from '../../../thread-utils';
import type { MessageParsedEmbeds } from '../../../ThreadMessage/threadTypes';
import { HighlightedEmbed } from '../HighlightedEmbed';
import { TopicContent } from '../v3/TopicContent';
import { TopicItemInteractions } from '../v3/TopicItemInteractions';
import TopicItemTitle from '../v3/TopicItemTitle';
import './CardStyles.scss';
import { CardViewTopicBottomActions } from './CardViewTopicBottomActions';

interface TopicListItemCardProps extends HTMLAttributes<HTMLDivElement> {
  topic: Topic;
  withRemove?: boolean;
  withActions?: boolean;
  isResponsive?: boolean;
  isSidebar?: boolean;
  itemTitleClassName?: string;
  shouldHandleDsaDisplay?: boolean;
}

const getProperHeightLimiter = (
  isMobileDisplay: boolean,
  highlightedType?: EjsHighlightedEmbed['type'],
) => {
  switch (true) {
    case ['image', 'embed'].some(t => highlightedType === t):
      return isMobileDisplay ? 120 : 170;
    case ['linkTool', 'googleForm'].some(t => highlightedType === t):
      return isMobileDisplay ? 300 : 350;
    default:
      return isMobileDisplay ? 400 : 450;
  }
};

export const TopicListItemCard: FC<TopicListItemCardProps> = memo(function TopicListItemCard(
  props,
) {
  const {
    topic,
    withRemove,
    withActions,
    className,
    isResponsive = false,
    isSidebar = false,
    itemTitleClassName = '',
    shouldHandleDsaDisplay = false,
    ...otherProps
  } = props;
  const { graphdebate } = useWebsite();
  const { push } = useConnectedRouter();
  // @ts-ignore
  const shouldIndexDiscussion = graphdebate?.meta?.topic?.index ?? 0;

  const [currentVotes, setCurrentVotes] = useState<Vote[]>((topic.votes as Vote[]) || []);

  const { locale } = useIntl();

  const displayedDate = getLocaleDateDigits(topic.createdAt, locale);

  const isMobileDisplay = useIsMobile();

  const { parsedEmbeds, cardViewEmbeds, cardViewHighlightedEmbeds } = useMemo(() => {
    let embeds: Partial<{
      parsedEmbeds: MessageParsedEmbeds | null;
      cardViewEmbeds: string;
      cardViewHighlightedEmbeds: EjsHighlightedEmbed | null;
    }> = {};

    const parsed = parseJSONString<MessageParsedEmbeds>(topic.embeds);
    embeds.parsedEmbeds = parsed;

    if (parsed) {
      const isOnlyOneBlock = parsed.blocks.length === 1;

      embeds.cardViewEmbeds = JSON.stringify(getCardViewContent(parsed));

      embeds.cardViewHighlightedEmbeds = !isOnlyOneBlock ? getEjsHighlightedEmbed(parsed, 1) : null;
    }
    return embeds;
  }, [topic.embeds]);

  const isOnlyOneBlock = parsedEmbeds?.blocks.length === 1;
  const shouldRemoveLimiter = isOnlyOneBlock && parsedEmbeds?.blocks[0].type !== 'paragraph';

  const isFullWidthContent =
    isOnlyOneBlock && ['embed', 'image'].includes(parsedEmbeds?.blocks[0].type);

  const handleRedirectToUnlimitedTopicPage = useCallback(() => {
    push({ pathname: `/t/${topic.slug}`, query: { cardRedirect: true } });
  }, [topic.slug, push]);

  return (
    <div
      className={twMerge(
        'TopicListItemCard flex flex-col text-gray-darkv4',
        shouldHandleDsaDisplay && [dsaClassHandler(topic.status), 'my-4'],
        className,
      )}
      {...otherProps}
    >
      <div className="topicContent flex flex-col gap-y-3">
        <div className="infos flex-1 overflow-hidden">
          <div className="head flex">
            <div
              className={twMerge(
                'post-infos flex items-center gap-x-1 text-[15px]',
                isSidebar && 'md:text-xs',
                isResponsive && 'md:text-[15px]',
              )}
            >
              <ProfilePopover
                userId={(topic.author as User).id}
                trigger={isMobileDisplay ? 'click' : 'mouseenter'}
                delay={[500, 0]}
              >
                <div className="flex items-center gap-x-1 md:gap-x-2">
                  <AuthorAvatar
                    author={topic.author}
                    userId={(topic.author as User).id}
                    profile={(topic.author as User).profile}
                    size="medium"
                    className={isResponsive ? 'md:w-10 md:h-10' : ''}
                  />
                  <div
                    className={twMerge(
                      'author font-medium cursor-pointer hover:text-primary',
                      isResponsive && 'md:font-semibold',
                    )}
                  >{`${getAuthorPseudo(topic.author)}, `}</div>
                </div>
              </ProfilePopover>

              <div
                className={twMerge(
                  'date text-gray-soft',
                  isSidebar && 'md:text-xs md:text-gray-400 md:font-medium',
                )}
              >
                <Tooltip
                  content={
                    <FormattedDate
                      value={new Date(topic.createdAt)}
                      year="numeric"
                      month="long"
                      day="numeric"
                      weekday="long"
                      hour="numeric"
                      minute="numeric"
                    />
                  }
                  touch={true}
                  touchTimeout={1500}
                >
                  <span>{displayedDate}</span>
                </Tooltip>
              </div>
            </div>
          </div>

          <TopicItemTitle
            topic={topic}
            isResponsive={isResponsive}
            itemTitleClassName={'text-[20px] mt-[5px] mb-[10px]'}
            className="font-medium"
            addLinkParams={{ cardRedirect: 'true' }}
          />

          <TopicContent
            heightLimit={getProperHeightLimiter(isMobileDisplay, cardViewHighlightedEmbeds?.type)}
            content={topic.content as string}
            embeds={cardViewEmbeds}
            status={topic.status as string}
            shouldHandleDsaDisplay={shouldHandleDsaDisplay}
            className={twMerge('m-0', isFullWidthContent && 'wideMedia')}
            onLimiterClick={handleRedirectToUnlimitedTopicPage}
            noLimiter={shouldRemoveLimiter}
          />
        </div>
        {cardViewHighlightedEmbeds && (
          <HighlightedEmbed embedToHighLight={cardViewHighlightedEmbeds} />
        )}

        {parsedEmbeds && (
          <EmbedsOverview messageEmbeds={parsedEmbeds} className="my-0" avoidSoleEmbedOverview />
        )}

        <TopicItemInteractions
          topic={topic}
          isResponsive={false}
          isSidebar={isSidebar}
          currentVotes={currentVotes}
        />
      </div>

      <CardViewTopicBottomActions topic={topic} onVoteUpdate={setCurrentVotes} />

      <DsaMessage
        contentType="topic"
        contentStatus={topic.status}
        moderationMotifId={topic.moderationMotifId}
        className={twMerge(
          'absolute top-0 right-4 transform -translate-y-1/3 px-3 rounded-full text-white text-sm',
          topic.status === 'pending' ? 'bg-gray-light' : 'bg-primary',
        )}
      />

      {shouldHandleDsaDisplay && topic.status !== 'approved' && (
        <ModerationUserVisibilityOnlyInfos
          contentType="message"
          className="absolute bottom-0 right-2 text-gray-medium text-sm"
        />
      )}
    </div>
  );
});
